import { JSX, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Swal from "sweetalert2";

import { LayoutUI } from "./layout.ui";
import { AppBarMenuSetting } from "../appBar/interfaces/AppBarMenuSetting";
import { useAuth } from "../../hooks/auth";
import { ApplicationRoutes } from "../../utils/navigation/applicationRoutes";
import { useLoginService } from "../../hooks/api/Login";

const getFirstAndLastName = (name: string) => {
  const names = name.trim().split(" ").filter(Boolean);
  if (names.length <= 1) {
    return name;
  }
  return `${names[0]} ${names[names.length - 1]}`;
};

export const LayoutContainer = ({ children }: { children: JSX.Element }) => {
  const [userName, setUserName] = useState("");
  const [isOpenSettings, setOpenSettings] = useState(false);
  const [menuSettings, setMenuSettings] = useState<AppBarMenuSetting[]>([]);

  const { signOut, getUserData, updateUserData } = useAuth();
  const { getMe, putLogout, putUpdatePassword, putUpdateUser } = useLoginService();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    handleCloseSettings();
    const result = await Swal.fire({
      title: "Tem certeza que deseja sair?",
      text: "Você precisará fazer login novamente para acessar o sistema.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, sair",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#10A8BF",
      cancelButtonColor: "#d33",
    });

    if (!result.isConfirmed) {
      return;
    }

    //TODO call singout endpoint
    await putLogout().catch(() => undefined);
    signOut();
    navigate(ApplicationRoutes.SIGN_IN);
  };

  const handleChangePassword = async () => {
    handleCloseSettings();
    const result = await Swal.fire({
      title: "Trocar senha",
      html:
        '<input id="currentPassword" type="password" class="swal2-input" placeholder="Senha atual">' +
        '<input id="newPassword" type="password" class="swal2-input" placeholder="Nova senha">' +
        '<input id="confirmPassword" type="password" class="swal2-input" placeholder="Confirmar nova senha">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#10A8BF",
      preConfirm: () => {
        const currentPassword = (
          document.getElementById("currentPassword") as HTMLInputElement
        )?.value;
        const newPassword = (
          document.getElementById("newPassword") as HTMLInputElement
        )?.value;
        const confirmPassword = (
          document.getElementById("confirmPassword") as HTMLInputElement
        )?.value;

        if (!currentPassword || !newPassword || !confirmPassword) {
          Swal.showValidationMessage("Preencha todos os campos.");
          return false;
        }

        if (newPassword.length < 8) {
          Swal.showValidationMessage(
            "A senha deve ter pelo menos 8 caracteres."
          );
          return false;
        }

        if (newPassword !== confirmPassword) {
          Swal.showValidationMessage("A confirmação da senha não confere.");
          return false;
        }

        return { currentPassword, newPassword };
      },
    });

    if (!result.isConfirmed || !result.value) {
      return;
    }

    try {
      const { id } = getUserData();
      await putUpdatePassword(id, result.value);
      await Swal.fire("Senha alterada", "Sua senha foi atualizada com sucesso.", "success");
    } catch (error: any) {
      await Swal.fire(
        "Erro ao trocar senha",
        error?.response?.data?.message || "Não foi possível alterar sua senha.",
        "error"
      );
    }
  };

  const handleEditPersonalData = async () => {
    handleCloseSettings();
    const currentUser = getUserData();
    const user = await getMe().catch(() => currentUser);
    const result = await Swal.fire({
      title: "Alterar dados pessoais",
      html:
        `<input id="profileName" class="swal2-input" placeholder="Nome" value="${user?.name || ""}">` +
        `<input id="profileEmail" class="swal2-input" placeholder="E-mail" value="${user?.email || ""}">` +
        `<input id="profilePhone" class="swal2-input" placeholder="Telefone" value="${user?.phone || ""}">`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#10A8BF",
      preConfirm: () => {
        const name = (document.getElementById("profileName") as HTMLInputElement)?.value;
        const email = (document.getElementById("profileEmail") as HTMLInputElement)?.value;
        const phone = (document.getElementById("profilePhone") as HTMLInputElement)?.value;

        if (!name || !email) {
          Swal.showValidationMessage("Nome e e-mail são obrigatórios.");
          return false;
        }

        return { name, email, phone };
      },
    });

    if (!result.isConfirmed || !result.value) {
      return;
    }

    try {
      await putUpdateUser(currentUser.id, result.value);
      updateUserData({ name: result.value.name, email: result.value.email });
      setUserName(getFirstAndLastName(result.value.name));
      await Swal.fire("Dados atualizados", "Seus dados foram alterados com sucesso.", "success");
    } catch (error: any) {
      await Swal.fire(
        "Erro ao alterar dados",
        error?.response?.data?.message || "Não foi possível alterar seus dados.",
        "error"
      );
    }
  };

  const fetchMenuSettings = () => {
    const menuSettings: AppBarMenuSetting[] = [
      {
        name: "Trocar senha",
        handleClick: handleChangePassword,
      },
      {
        name: "Alterar dados",
        handleClick: handleEditPersonalData,
      },
      {
        name: "Sair",
        handleClick: handleSignOut,
      },
    ];

    setMenuSettings(menuSettings);
  };

  const handleOpenSettings = () => {
    setOpenSettings(true);
  };

  const handleCloseSettings = () => {
    setOpenSettings(false);
  };

  useEffect(() => {
    fetchMenuSettings();
    const { name } = getUserData();
    setUserName(getFirstAndLastName(name));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LayoutUI
      appBarProps={{
        userName,
        isOpenSettings,
        menuSettings,
        handleOpenSettings,
        handleCloseSettings,
      }}
    >
      {children}
    </LayoutUI>
  );
};
